/**
 * ByteBuffer — TypeScript port of base/bytebuffer.{h,cpp}.
 *
 * The WI wire protocol is BIG-ENDIAN (network byte order): the C++ ByteBuffer applies
 * htons/htonl on word/dword writes and ntohs/ntohl on reads (bytebuffer.cpp:98-106,33-51).
 * Bytes are raw. This is a faithful sequential reader/writer over a Uint8Array, used by
 * xmsg.ts / netmessage.ts / messages.ts to encode and decode frames byte-for-byte.
 *
 * Validated by cloudflare/test/protocol/*.test.ts against the golden vectors captured from
 * the real C++ wis server (cloudflare/test/vectors/mp/).
 */

export class ByteWriter {
  private bytes: number[] = [];

  writeByte(b: number): void {
    this.bytes.push(b & 0xff);
  }

  // word = u16 big-endian (htons)
  writeWord(w: number): void {
    this.bytes.push((w >>> 8) & 0xff, w & 0xff);
  }

  // dword = u32 big-endian (htonl)
  writeDword(dw: number): void {
    this.bytes.push((dw >>> 24) & 0xff, (dw >>> 16) & 0xff, (dw >>> 8) & 0xff, dw & 0xff);
  }

  writeBytes(b: Uint8Array | number[]): void {
    for (const x of b) this.bytes.push(x & 0xff);
  }

  /**
   * Write a string. Mirrors ByteBuffer::WriteString (bytebuffer.cpp:108): writes the bytes
   * plus (by default) a trailing NUL. Encodes as latin1/raw bytes so it round-trips the
   * server's char[] fields exactly (the protocol is byte-oriented, not UTF-8 aware here).
   */
  writeString(s: string, zero = true): void {
    for (let i = 0; i < s.length; i++) this.bytes.push(s.charCodeAt(i) & 0xff);
    if (zero) this.bytes.push(0);
  }

  /** Fixed-width char field: `s` truncated/NUL-padded to exactly `width` bytes. */
  writeFixedString(s: string, width: number): void {
    for (let i = 0; i < width; i++) {
      this.bytes.push(i < s.length ? s.charCodeAt(i) & 0xff : 0);
    }
  }

  get length(): number {
    return this.bytes.length;
  }

  toUint8Array(): Uint8Array {
    return Uint8Array.from(this.bytes);
  }
}

export class ByteReader {
  private pos = 0;
  private readonly buf: Uint8Array;
  constructor(buf: Uint8Array) {
    this.buf = buf;
  }

  get remaining(): number {
    return this.buf.length - this.pos;
  }
  get position(): number {
    return this.pos;
  }

  readByte(): number {
    if (this.remaining < 1) throw new RangeError('readByte: underflow');
    return this.buf[this.pos++];
  }

  // word = u16 big-endian (ntohs)
  readWord(): number {
    if (this.remaining < 2) throw new RangeError('readWord: underflow');
    const w = (this.buf[this.pos] << 8) | this.buf[this.pos + 1];
    this.pos += 2;
    return w >>> 0;
  }

  // dword = u32 big-endian (ntohl)
  readDword(): number {
    if (this.remaining < 4) throw new RangeError('readDword: underflow');
    const dw =
      this.buf[this.pos] * 0x1000000 + // avoid sign issues with <<24
      (this.buf[this.pos + 1] << 16) +
      (this.buf[this.pos + 2] << 8) +
      this.buf[this.pos + 3];
    this.pos += 4;
    return dw >>> 0;
  }

  readBytes(cb: number): Uint8Array {
    if (cb > this.remaining) throw new RangeError('readBytes: underflow');
    const out = this.buf.slice(this.pos, this.pos + cb);
    this.pos += cb;
    return out;
  }

  /** Read exactly `width` bytes and return the NUL-terminated string within (latin1). */
  readFixedString(width: number): string {
    const raw = this.readBytes(width);
    let end = raw.indexOf(0);
    if (end < 0) end = raw.length;
    let s = '';
    for (let i = 0; i < end; i++) s += String.fromCharCode(raw[i]);
    return s;
  }

  /** Read a `[u16 cb][cb bytes incl NUL]` length-prefixed string (XMsgS* layout). */
  readPrefixedString(): string {
    const cb = this.readWord();
    const raw = this.readBytes(cb);
    let end = raw.indexOf(0);
    if (end < 0) end = raw.length;
    let s = '';
    for (let i = 0; i < end; i++) s += String.fromCharCode(raw[i]);
    return s;
  }
}
